import { useState } from "react";
import axios from "axios";
import { Download, Lock, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (pw) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "x-admin-password": pw },
      });
      setRows(res.data);
      setAuthed(true);
    } catch (e) {
      setError("Invalid password.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    load(password);
  };

  const exportCsv = () => {
    window.location.href = `${API}/admin/submissions/export?password=${encodeURIComponent(
      password
    )}`;
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 rsm-grid-bg">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#121927] p-8"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-amber-400/40 bg-amber-500/10">
            <Lock size={20} className="text-amber-400" />
          </div>
          <h1 className="mt-6 font-display text-2xl font-bold text-white">
            Admin Access
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            Enter the admin password to view contact submissions.
          </p>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-6 w-full rounded-lg border border-white/10 bg-[#0a0d14] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-400/60"
          />
          {error && (
            <p data-testid="admin-error" className="mt-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-[#0a0d14] hover:bg-amber-400 transition-colors disabled:opacity-60"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-white">
              Contact Submissions
            </h1>
            <p className="mt-1 text-sm text-slate-400" data-testid="admin-count">
              {rows.length} record{rows.length === 1 ? "" : "s"} — consent proof
              of opt-in
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(password)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white hover:border-amber-400/50 transition-colors"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-[#0a0d14] hover:bg-amber-400 transition-colors"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-left text-sm" data-testid="admin-table">
            <thead className="bg-[#121927] text-xs uppercase tracking-wider text-slate-400">
              <tr>
                {[
                  "Timestamp (UTC)",
                  "Name",
                  "Email",
                  "Phone",
                  "Company",
                  "Interest",
                  "SMS",
                  "Email",
                  "IP",
                  "Message",
                  "Consent Text",
                ].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-slate-500">
                    No submissions yet.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="text-slate-300 hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-400">
                    {r.timestamp}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {r.firstName} {r.lastName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{r.email}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.mobilePhone || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.company || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.interest}</td>
                  <td className="px-4 py-3">
                    <Badge on={r.smsConsent} />
                  </td>
                  <td className="px-4 py-3">
                    <Badge on={r.emailConsent} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-400">
                    {r.ipAddress || "—"}
                  </td>
                  <td className="max-w-xs px-4 py-3 text-xs text-slate-400">
                    {r.message || "—"}
                  </td>
                  <td className="max-w-md px-4 py-3 text-[11px] text-slate-500">
                    <div>
                      <span className="text-amber-400/70">SMS:</span>{" "}
                      {r.smsConsentText}
                    </div>
                    <div className="mt-1">
                      <span className="text-amber-400/70">Email:</span>{" "}
                      {r.emailConsentText}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Badge = ({ on }) => (
  <span
    className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-mono ${
      on
        ? "bg-amber-500/15 text-amber-300"
        : "bg-white/5 text-slate-500"
    }`}
  >
    {on ? "YES" : "no"}
  </span>
);
